import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  cancelAppointmentOrder,
  getAppointmentOrders,
  resetAppointmentOrder
} from '../api/reportApi';

const pageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(18rem, 26rem)',
  gap: '1rem',
  alignItems: 'start'
};

const rowBaseStyle = {
  width: '100%',
  textAlign: 'left',
  border: '0',
  background: 'transparent',
  padding: '1rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  color: 'inherit',
  cursor: 'pointer'
};

const inspectorMetaStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(6rem, 7rem) 1fr',
  gap: '0.9rem',
  padding: '0.8rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
};

const AppointmentOrdersQueue = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceClinicFilter, setSourceClinicFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const data = await getAppointmentOrders(params);
      setOrders(data || []);
      setSelectedOrderId((current) => {
        const nextOrders = data || [];
        if (!nextOrders.length) {
          return null;
        }
        if (current && nextOrders.some((order) => order.id === current)) {
          return current;
        }
        return nextOrders[0].id;
      });
    } catch (requestError) {
      console.error('获取预约单队列失败:', requestError);
      setError(
        isZh
          ? `获取预约单队列失败：${requestError.response?.data?.error || requestError.message || '未知错误'}`
          : `Failed to load appointment orders: ${requestError.response?.data?.error || requestError.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedStatus = params.get('status');
    const requestedOrderId = Number(params.get('order_id'));
    const validStatuses = ['pending', 'claimed', 'completed', 'cancelled', 'all'];

    if (requestedStatus && validStatuses.includes(requestedStatus)) {
      setStatusFilter(requestedStatus);
      return;
    }

    if (requestedOrderId > 0) {
      setStatusFilter('all');
    }
  }, [location.search]);

  const clinicOptions = useMemo(() => {
    const clinicMap = new Map();
    orders.forEach((order) => {
      const clinicId = order.source_clinic_id || 'unknown';
      const clinicLabel = order.source_clinic_name || (order.source_clinic_id ? `Clinic #${order.source_clinic_id}` : (isZh ? '未注明诊所' : 'Unknown clinic'));
      clinicMap.set(String(clinicId), clinicLabel);
    });
    return Array.from(clinicMap.entries()).map(([value, label]) => ({ value, label }));
  }, [orders, isZh]);

  const filteredOrders = useMemo(() => {
    if (sourceClinicFilter === 'all') {
      return orders;
    }
    if (sourceClinicFilter === 'unknown') {
      return orders.filter((order) => !order.source_clinic_id);
    }
    return orders.filter((order) => String(order.source_clinic_id) === sourceClinicFilter);
  }, [orders, sourceClinicFilter]);

  const selectedOrder = filteredOrders.find((order) => order.id === selectedOrderId) || filteredOrders[0] || null;

  useEffect(() => {
    if (!selectedOrder && filteredOrders.length > 0) {
      setSelectedOrderId(filteredOrders[0].id);
    }
  }, [filteredOrders, selectedOrder]);

  useEffect(() => {
    const requestedOrderID = Number(new URLSearchParams(location.search).get('order_id'));
    if (requestedOrderID > 0 && filteredOrders.some((order) => order.id === requestedOrderID)) {
      setSelectedOrderId(requestedOrderID);
    }
  }, [filteredOrders, location.search]);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return isZh ? '未记录' : 'Not recorded';
    }
    return new Date(dateValue).toLocaleString(isZh ? 'zh-CN' : 'en-US');
  };

  const formatClinic = (name, id) => {
    if (name) {
      return name;
    }
    if (id) {
      return `Clinic #${id}`;
    }
    return isZh ? '未注明诊所' : 'Unknown clinic';
  };

  const formatStatus = (status) => {
    if (isZh) {
      return {
        pending: '待认领',
        claimed: '已认领',
        completed: '已完成',
        cancelled: '已取消'
      }[status] || status;
    }

    return {
      pending: 'Pending',
      claimed: 'Claimed',
      completed: 'Completed',
      cancelled: 'Cancelled'
    }[status] || status;
  };

  const getStatusClass = (status) => {
    if (status === 'claimed') return 'status-pill--cool';
    if (status === 'completed') return 'status-pill--success';
    if (status === 'cancelled') return 'status-pill--danger';
    return 'status-pill--warning';
  };

  const statusCounts = useMemo(() => {
    return orders.reduce((accumulator, order) => {
      accumulator[order.status] = (accumulator[order.status] || 0) + 1;
      return accumulator;
    }, {});
  }, [orders]);

  const getClaimVisitLink = (order) => {
    if (!order?.claim_visit_id) {
      return null;
    }

    return order.status === 'claimed'
      ? `/reports?visit_id=${order.claim_visit_id}`
      : `/reviewed-reports?visit_id=${order.claim_visit_id}`;
  };

  const handleOrderAction = async (action) => {
    if (!selectedOrder) {
      return;
    }

    const confirmMessage = action === 'cancel'
      ? (isZh ? '确认要取消这张预约单吗？' : 'Cancel this appointment order?')
      : (isZh ? '确认要把这张已认领的预约单退回待认领吗？' : 'Reset this claimed order back to pending?');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setActing(true);
      setError('');
      setSuccessMessage('');

      if (action === 'cancel') {
        await cancelAppointmentOrder(selectedOrder.id);
      } else {
        await resetAppointmentOrder(selectedOrder.id);
      }

      setSuccessMessage(
        action === 'cancel'
          ? (isZh ? '预约单已取消' : 'Appointment order cancelled')
          : (isZh ? '预约单已退回待认领状态' : 'Appointment order reset to pending')
      );

      await fetchOrders();
    } catch (requestError) {
      console.error('预约单操作失败:', requestError);
      setError(
        isZh
          ? `预约单操作失败：${requestError.response?.data?.error || requestError.message || '未知错误'}`
          : `Appointment order update failed: ${requestError.response?.data?.error || requestError.message || 'Unknown error'}`
      );
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <span className="page-eyebrow">{isZh ? '未来预约队列' : 'Future Queue'}</span>
          <h1 className="page-title">{isZh ? '复诊预约单工作台' : 'Appointment Orders Queue'}</h1>
          <p className="page-subtitle">
            {isZh
              ? '用全局视角查看未来复诊预约单，明确区分开单诊所、接诊诊所和当前状态，让跨诊所复诊也能快速分流。'
              : 'Work the future follow-up queue from one console, with source clinic, claim clinic, and order status aligned for cross-clinic follow-up routing.'}
          </p>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={fetchOrders}>
            {isZh ? '刷新队列' : 'Refresh queue'}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="status-banner status-banner--success">
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--warning">{isZh ? `待认领 ${statusCounts.pending || 0}` : `${statusCounts.pending || 0} pending`}</span>
            <span className="status-pill status-pill--cool">{isZh ? `已认领 ${statusCounts.claimed || 0}` : `${statusCounts.claimed || 0} claimed`}</span>
            <span className="status-pill status-pill--success">{isZh ? `已完成 ${statusCounts.completed || 0}` : `${statusCounts.completed || 0} completed`}</span>
          </div>

          <div className="filter-row">
            <div className="field">
              <label className="field__label" htmlFor="appointment-status-filter">
                {isZh ? '状态' : 'Status'}
              </label>
              <select
                id="appointment-status-filter"
                className="theme-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="pending">{isZh ? '待认领' : 'Pending'}</option>
                <option value="claimed">{isZh ? '已认领' : 'Claimed'}</option>
                <option value="completed">{isZh ? '已完成' : 'Completed'}</option>
                <option value="cancelled">{isZh ? '已取消' : 'Cancelled'}</option>
                <option value="all">{isZh ? '全部状态' : 'All statuses'}</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="appointment-clinic-filter">
                {isZh ? '来源诊所' : 'Source clinic'}
              </label>
              <select
                id="appointment-clinic-filter"
                className="theme-select"
                value={sourceClinicFilter}
                onChange={(event) => setSourceClinicFilter(event.target.value)}
              >
                <option value="all">{isZh ? '全部诊所' : 'All clinics'}</option>
                {clinicOptions.map((clinic) => (
                  <option key={clinic.value} value={clinic.value}>
                    {clinic.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      <div className="appointment-queue-grid" style={pageGridStyle}>
        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                  {isZh ? '预约单队列' : 'Order queue'}
                </h2>
                <p style={{ margin: '0.35rem 0 0', color: 'var(--text-secondary)' }}>
                  {isZh ? `当前显示 ${filteredOrders.length} 条记录` : `${filteredOrders.length} orders shown`}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-orb" />
                <span>{isZh ? '正在加载预约单队列…' : 'Loading appointment orders...'}</span>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="empty-state">
                <h3 className="auth-form__title">{isZh ? '当前没有匹配的预约单' : 'No appointment orders found'}</h3>
                <p className="auth-form__subtitle">
                  {isZh
                    ? '可以调整状态或诊所筛选，再次刷新查看。'
                    : 'Try another status or clinic filter, then refresh the queue again.'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid' }}>
                {filteredOrders.map((order, index) => (
                  <button
                    key={order.id}
                    type="button"
                    className={`appointment-queue-row ${selectedOrder?.id === order.id ? 'is-selected' : ''}`}
                    style={{
                      ...rowBaseStyle,
                      animationDelay: `${index * 28}ms`
                    }}
                    onClick={() => setSelectedOrderId(order.id)}
                  >
                    <div style={{ display: 'grid', gap: '0.45rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '0.55rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`status-pill ${getStatusClass(order.status)}`}>{formatStatus(order.status)}</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {order.description || order.check_type}
                          </span>
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          #{order.id}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
                        <span>{order.patient_name || (isZh ? '未命名患者' : 'Unnamed patient')}</span>
                        <span>{formatClinic(order.source_clinic_name, order.source_clinic_id)}</span>
                        <span>{isZh ? `来源 Visit #${order.source_visit_id}` : `Source visit #${order.source_visit_id}`}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                        <span>
                          {isZh
                            ? `接诊诊所：${formatClinic(order.claim_clinic_name, order.claim_clinic_id)}`
                            : `Claim clinic: ${formatClinic(order.claim_clinic_name, order.claim_clinic_id)}`}
                        </span>
                        <span>{formatDate(order.created_at)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="surface-panel surface-panel--soft">
          <div className="surface-panel__body stack">
            <div>
              <span className="page-eyebrow">{isZh ? '详情检视' : 'Inspector'}</span>
              <h2 style={{ margin: '0.45rem 0 0', fontSize: '1.3rem', color: 'var(--text-primary)' }}>
                {selectedOrder
                  ? (selectedOrder.description || selectedOrder.check_type)
                  : (isZh ? '选择一张预约单' : 'Select an order')}
              </h2>
            </div>

            {!selectedOrder ? (
              <p className="auth-form__subtitle">
                {isZh
                  ? '从左侧队列选择一张预约单后，这里会显示来源诊所、认领信息和可执行操作。'
                  : 'Select an order from the queue to inspect source clinic, claim info, and available actions.'}
              </p>
            ) : (
              <>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '患者' : 'Patient'}</strong>
                  <span>{selectedOrder.patient_name || (isZh ? '未命名患者' : 'Unnamed patient')}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '来源诊所' : 'Source clinic'}</strong>
                  <span>{formatClinic(selectedOrder.source_clinic_name, selectedOrder.source_clinic_id)}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '来源 Visit' : 'Source visit'}</strong>
                  <span>#{selectedOrder.source_visit_id}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '接诊诊所' : 'Claim clinic'}</strong>
                  <span>{formatClinic(selectedOrder.claim_clinic_name, selectedOrder.claim_clinic_id)}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '认领 Visit' : 'Claim visit'}</strong>
                  <span>{selectedOrder.claim_visit_id ? `#${selectedOrder.claim_visit_id}` : (isZh ? '尚未认领' : 'Not claimed yet')}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '状态' : 'Status'}</strong>
                  <span>{formatStatus(selectedOrder.status)}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '创建时间' : 'Created at'}</strong>
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
                <div style={inspectorMetaStyle}>
                  <strong style={{ color: 'var(--text-muted)' }}>{isZh ? '说明' : 'Description'}</strong>
                  <span>{selectedOrder.description || (isZh ? '无额外说明' : 'No additional note')}</span>
                </div>

                <div className="inline-link-row">
                  {selectedOrder.source_report_id && (
                    <Link className="inline-link" to={`/reports/${selectedOrder.source_report_id}/view`}>
                      {isZh ? '查看来源报告' : 'Open source report'}
                    </Link>
                  )}
                  <Link className="inline-link" to={`/reviewed-reports?visit_id=${selectedOrder.source_visit_id}`}>
                    {isZh ? '查看来源 Visit 记录' : 'Open source visit history'}
                  </Link>
                  {selectedOrder.claim_visit_id && (
                    <Link className="inline-link" to={getClaimVisitLink(selectedOrder)}>
                      {selectedOrder.status === 'claimed'
                        ? (isZh ? '查看接诊 Visit 队列' : 'Open claim visit queue')
                        : (isZh ? '查看接诊 Visit 记录' : 'Open claim visit history')}
                    </Link>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {selectedOrder.status === 'pending' && (
                    <button
                      type="button"
                      className="theme-button theme-button--danger"
                      disabled={acting}
                      onClick={() => handleOrderAction('cancel')}
                    >
                      {isZh ? '取消预约单' : 'Cancel order'}
                    </button>
                  )}

                  {selectedOrder.status === 'claimed' && (
                    <button
                      type="button"
                      className="theme-button theme-button--secondary"
                      disabled={acting}
                      onClick={() => handleOrderAction('reset')}
                    >
                      {isZh ? '退回待认领' : 'Reset to pending'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      <style>{`
        .appointment-queue-row {
          animation: appointment-row-enter 320ms ease both;
        }

        .appointment-queue-row.is-selected {
          padding-left: 0.75rem;
          border-color: rgba(67, 214, 197, 0.24) !important;
        }

        .appointment-queue-row:hover {
          transform: translateY(-1px);
        }

        @media (max-width: 1080px) {
          .appointment-queue-row {
            padding-left: 0;
          }
        }

        @media (max-width: 1080px) {
          .appointment-queue-grid {
            grid-template-columns: 1fr;
          }
        }

        @keyframes appointment-row-enter {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AppointmentOrdersQueue;
